<script>
   /*************************************************************/
   /* AppContainer for alternative test                         */
   /* ---------------------------------                         */
   /* app container for applying DD-SIMCA model to alt class    */
   /*************************************************************/

   import { untrack } from 'svelte';
   import { pcamodel_get, pcares_get } from '@mdatools-apps/wasm/pca.js';
   import { app_residuals } from '@mdatools-apps/shared/utils';
   import { Container, Button } from 'svelte-controls-basic';
   import { downloadResults, downloadModel, predict } from '@mdatools-apps/shared/methods/simca.js';

   import DataLoader from '@mdatools-apps/shared/components/pca/DataLoader.svelte';
   import Outliers from '@mdatools-apps/shared/widgets/Outliers.svelte';
   import Contributions from '@mdatools-apps/shared/components/Contributions.svelte';

   import ModelLoader from '../shared/ModelLoader.svelte';
   import ModelTable from '../shared/ModelTable.svelte';
   import SIMCASettings from '../shared/SIMCASettings.svelte';

   import ResultTable from './ResultTable.svelte';
   import AppPlots from './AppPlots.svelte';

   // WASM promise and session object from parent app
   let {WASM, backupSession, session = $bindable()} = $props();

   // get part of the session for this tab
   const s = $derived(session['new']);

   // properties for showing contributions
   let selected = $state(-1);
   let contributionIsShown = $state(false);


   // callback function to set object with a give position in plot series
   // as outlier
   function markOutlier(pos) {

      // check that position is valid
      if (pos < 0 || pos > s.newres.pca.nrows - 1) return;

      // find sample ID corresponding to the position
      // this is needed if some of the objects already
      // removed from results as outliers
      const id = s.newres.pca.objindices[pos];

      // add the object to outliers array and reset the model and all related outcomes
      if (id >= 0 && !s.outliers.includes(id)) {
         s.outliers.push(id);
         s.newres.simca = null;
         s.newres.pca = null;
      }
   }



   // callback to reset outliers
   function resetOutliers() {
      s.outliers = [];
      s.newres.simca = null;
      s.newres.pca = null;
   }

   // callback to undo outliers
   function undoOutliers() {
      s.outliers.pop();
      s.newres.simca = null;
      s.newres.pca = null;
   }


   // show modal dialog with contributions for selected object
   function showContributions(pos) {
      if (pos < 0) return;
      selected = pos;
      contributionIsShown = true;
   }

   // hide modal dialog with contributions
   function hideContributions() {
      contributionIsShown = false;
      selected = -1;
   }


   // pre-rendering effect to avoid bad structure of the session object
   $effect.pre(() => {

      // if new set is set null - reset results and all related parameters
      if (s.newset === null) {
         s.outliers = [];
         s.newres.simca = null;
         s.newres.pca = null;
      }

      // if model object is set to null - reset dependent objects
      if (s.model === null) {
         s.outliers = [];
         s.simca = null;
         s.newres.simca = null;
         s.newres.pca = null;
         s.newset = null;
      }
   });

   // if model object is set to null - reset dependent objects
   $effect(() => {
      if (s.model === null) {
         s.outliers = [];
         s.newres.simca = null;
         s.newres.pca = null;
         s.newset = null;
         s.simca = null;
      }
   });

   // if new set is set null - reset results and all related parameters
   $effect(() => {
      if (s.newset === null) {
         s.outliers = [];
         s.newres.simca = null;
         s.newres.pca = null;
      }
   });


   // effects to track changes in eCrit and oCrit for recalculations of simca results
   $effect(() => {
      if (s.newres.pca && s.simca) {
         s.newres.simca = predict(
            s.newres.pca,
            untrack(() => s.simca.limType),
            s.simca.eCrit,
            s.simca.oCrit
         )
      }
   });

   // reactive effect for backing up the session. the session is read by 'backupSession' when the
   // write is due, not here, and the first run is skipped: it only ever coincides with a session
   // the app was given as a whole, which is already on disk, is written by 'MainApp' itself, or is
   // a fresh one that need not be stored. the values after the function are the dependencies (see
   // 'backupSession')
   let mounted = false;
   $effect(() => {
      const deps = (s.simca && s.newres) ? [s.model, s.newset, s.newres.simca, s.simca.ncomp] :
         [s.model, s.newset];
      if (mounted) backupSession(() => session, ...deps);
      mounted = true;
   });

</script>

{#if contributionIsShown && s.model && s.simca && s.newres.pca}
   <Contributions
      model_getter={pcamodel_get}
      res_getter={pcares_get}
      model={s.model}
      res={{'new': s.newres.pca}}
      residuals={() => WASM().then((M) =>
         app_residuals(M, s.model, s.newset, s.newres.pca, true))}
      closeModal={hideContributions}
      ncomp={s.simca.ncomp}
      {selected}
   />
{/if}

<div class="app-container">

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="model-loader">
         <ModelLoader bind:model={s.model} bind:simca={s.simca} />
      </div>

      {#if s.model}
      <div class="data-loader">
         <DataLoader
            bind:dataset={s.newset}
            bind:resobj={s.newres.pca}
            outliers={s.outliers}
            {WASM}
            model={s.model}
            className="Class"
            csvOnly={false}
         />
      </div>
      {/if}

      {#if s.outliers && s.outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers outliers={s.outliers} dataset={s.newset} {undoOutliers} {resetOutliers} />
      </div>
      {/if}

      <div class="simca-settings">

         {#if s.model && s.newres.pca }
         <SIMCASettings
            model={s.model}
            bind:simca={s.simca}
            isAlt={true}
         />
         {/if}

         {#if s.model && s.simca && s.newres.simca }
         <Container label='Download'>
            <div class="buttons">
            <Button onclick={() => downloadResults(s.model, s.simca, s.newres, s.newset, "new")} text="Res (new)"/>
            <Button onclick={() => downloadModel(s.model, s.simca)} text="Model" />
            </div>
         </Container>
         {/if}
      </div>

      {#if s.simca && s.model && s.newres.simca}
      <div class="model-stats">
         <ModelTable model={s.model} simca={s.simca} isAlt={true}/>
      </div>
      <div class="result-stats">
         <ResultTable res={s.newres.simca} ncomp={s.simca.ncomp}/>
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !s.newres.simca}
         <div class="help-text">
         <p>
            In this tab you can apply previously trained DD-SIMCA model to a new dataset with objects whose class is unknown (hence the app
            does not expect column with target classes).
         </p>
         {#if !s.model}
            <p><strong>Step 1</strong></p>
            <p>
               Load previously trained DD-SIMCA model from file.
            </p>
         {:else}
            {#if !s.newset}
            <p><strong>Step 2</strong></p>
            <p>
               Load data set from CSV file. The requirements to the file are the same as for the
               training set but no columns with reference classes is expected. If you have reference classes, use <em>Test</em> tab.
            </p>
            {/if}
         {/if}
         </div>
      {:else}
         {#if s.model && s.simca && s.newres.pca}
         {#key s.newres.pca }
         <AppPlots {markOutlier} {showContributions} {...s} />
         {/key}
         {/if}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0.25em 0.25em;
      height: 100%;
   }

   .app-controls > div {
      border-radius: 0.25rem;
   }

   .app-controls > .model-stats,
   .app-controls > .result-stats {
      background: #e8e8e8;
      padding: 0.5rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.85em;
   }


   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }



   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(325px, 30%) 1fr;
      }
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

</style>